// src/stage2/conversation/NPCInteractable.jsx
import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { useConversation } from "./ConversationContext";

const tmpWorld = new THREE.Vector3();

export default function NPCInteractable(props) {
  const {
    npcId = "npc",
    radius = 2.2,
    prompt = "Interact with this Character",
    onInteractText = "Ride a bike!",
    debug = false, 

    children,
    playerPosRef,
    targetRef, // optional: the *moving* group ref (PathMotion group)
  } = props;

  useEffect(() => {
    if (!debug) return;
    console.log(`[NPC:${npcId}] mounted ✅`, { radius, prompt, onInteractText });
    return () => console.log(`[NPC:${npcId}] unmounted ❌`);
  }, [npcId, radius, prompt, onInteractText, debug]);

  const wrapperRef = useRef();

  const { registerNear, unregisterNear, activeId, endTalk, near } = useConversation();
  const isLocked = activeId === npcId;

  const wasNearRef = useRef(false);
  const tickRef = useRef(0);

  // ✅ Optional safety: unregister on unmount
  useEffect(() => {
    return () => unregisterNear?.(npcId);
  }, [npcId, unregisterNear]);

  useFrame(() => {
    const targetObj = targetRef?.current || wrapperRef.current;
    if (!targetObj) return;

    const player = playerPosRef?.current;
    if (!player) {
      if (wasNearRef.current) {
        wasNearRef.current = false;
        unregisterNear(npcId);
      }
      return;
    }

    targetObj.getWorldPosition(tmpWorld);

    const dx = tmpWorld.x - player.x;
    const dz = tmpWorld.z - player.z;
    const d = Math.hypot(dx, dz);

    tickRef.current++;
    if (debug && tickRef.current % 30 === 0) {
      console.log(`[NPC:${npcId}] dist=${d.toFixed(2)} r=${radius} locked=${isLocked}`);
    }

    const isNear = d <= radius;

    // -------------------------
    // LOCKED behavior
    // -------------------------
    if (isLocked) {
      if (isNear) {
        wasNearRef.current = true;
        // ✅ keep candidate fresh while locked
        registerNear({ id: npcId, prompt, text: onInteractText, d });
      } else if (wasNearRef.current) {
        wasNearRef.current = false;
        // ✅ don't call clearNear; use unregisterNear
        unregisterNear(npcId);
        endTalk?.(npcId);
      }
      return;
    }

    // -------------------------
    // UNLOCKED behavior
    // -------------------------
    if (isNear) {
      wasNearRef.current = true;
      registerNear({ id: npcId, prompt, text: onInteractText, d });
      return;
    }

    if (wasNearRef.current) {
      wasNearRef.current = false;
      unregisterNear(npcId);
      endTalk?.(npcId);
    }
  });

  return <group ref={wrapperRef}>{children}</group>;
}
